import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EmailDomainsApi } from '@/api/clients/email-domains/email-domains.api';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import CardFooter from '@/components/Card/CardFooter';
import Button from '@/components/CustomButtons/Button';
import CustomInput from '@/components/CustomInput/CustomInput';
import InfoTooltip from '@/components/InfoTooltip/InfoTooltip';
import { Delete } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
} from '@mui/material';

export default function AudienceCard({
  formData,
  handleChange,
  handleLogoUpload,
  clearLogo,
  handleCheckboxChange,
  addIdentifier,
  onIdentifierChange,
  removeIdentifier,
  isCreate,
  isEditingAudience,
  setIsEditingAudience,
  handleSubmit,
  loading,
  setFormData,
}) {
  const { t } = useTranslation();
  const [originalFormData, setOriginalFormData] = useState(null);
  const [organizationDomains, setOrganizationDomains] = useState([]);

  const inputsDisabled = !isCreate && !isEditingAudience;

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await EmailDomainsApi.getOrganizationDomains();
        setOrganizationDomains(response.data.domains || []);
      } catch (error) {
        console.error('Error fetching organization domains:', error);
        setOrganizationDomains([]);
      }
    };

    fetchDomains();
  }, []);

  const handleEnterEditMode = () => {
    setOriginalFormData({ ...formData });
    setIsEditingAudience(true);
  };

  const handleCancelEdit = () => {
    if (originalFormData) {
      setFormData(originalFormData);
    }
    setIsEditingAudience(false);
  };

  const handleEmailLocalPartChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      email_local_part: e.target.value,
    }));
  };

  const handleEmailDomainChange = (event) => {
    const domainId = event.target.value;
    console.log(event);

    setFormData((prevData) => ({
      ...prevData,
      email_domain_id: domainId,
    }));
  };

  return (
    <Card>
      <CardHeader color='primary'>
        <h4>
          {isCreate ? t('audience.createAudience') : t('audience.editAudience')}
        </h4>
      </CardHeader>
      <CardBody>
        <form onSubmit={handleSubmit}>
          <GridContainer>
            <GridItem xs={12} sm={12} md={6}>
              <CustomInput
                labelText={t('audience.audienceName')}
                id='name'
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  disabled: inputsDisabled,
                  name: 'name',
                  value: formData.name,
                  onChange: handleChange,
                }}
              />
            </GridItem>
          </GridContainer>

          <GridContainer>
            {formData.audience_identifiers.map((identifier, index) => (
              <GridItem xs={12} sm={12} md={3} key={index}>
                <CustomInput
                  onChange={(e) => onIdentifierChange(e, index)}
                  labelText={t('audience.audienceIdentifier', {
                    number: index + 1,
                  })}
                  formControlProps={{}}
                  inputProps={{
                    disabled: inputsDisabled,
                    value: identifier,
                  }}
                />
                {!inputsDisabled && (
                  <Delete
                    onClick={() => removeIdentifier(index)}
                    style={{ cursor: 'pointer' }}
                  />
                )}
              </GridItem>
            ))}
          </GridContainer>
          {!inputsDisabled && (
            <Button color='primary' onClick={addIdentifier}>
              {t('audience.addAudienceIdentifier')}
            </Button>
          )}
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              {!inputsDisabled && (
                <div className='custom-upload m-b-30 m-t-27'>
                  <label htmlFor='logo'>{t('audience.uploadLogo')}</label>
                  <br />
                  <label htmlFor='logo' className='custom-upload-btn'>
                    <span>{t('common.buttons.browse')}</span>
                    <input
                      id='logo'
                      name='logo'
                      type='file'
                      accept='image/*'
                      style={{ display: 'none' }}
                      onChange={handleLogoUpload}
                      disabled={inputsDisabled}
                    />
                  </label>
                </div>
              )}
              {formData.logoPreview && (
                <div className='preview-image' width='130'>
                  <img
                    src={formData.logoPreview}
                    alt={t('audience.logoPreview')}
                    width='130'
                  />
                </div>
              )}
              {formData.logoPreview && !inputsDisabled && (
                <span
                  className='material-icons'
                  style={{ cursor: 'pointer' }}
                  onClick={clearLogo}>
                  delete
                </span>
              )}
              <div style={{ display: 'flex', marginBottom: '20px' }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name='include_cpd_logo'
                        onChange={handleCheckboxChange}
                        checked={formData.include_cpd_logo}
                        disabled={inputsDisabled}
                      />
                    }
                    label={t('audience.includeCpdLogo')}
                  />
                </FormGroup>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name='use_business_unit_logo'
                        onChange={handleCheckboxChange}
                        checked={formData.use_business_unit_logo}
                        disabled={inputsDisabled}
                      />
                    }
                    label={t('audience.useBusinessUnitLogo')}
                  />
                </FormGroup>
              </div>
              <GridContainer>
                <GridItem xs={12} sm={12} md={6}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '10px',
                    }}>
                    <CustomInput
                      disabled={inputsDisabled}
                      type='text'
                      labelText={t('emailTemplates.emailSender.emailLocalPart')}
                      onChange={handleEmailLocalPartChange}
                      value={formData.email_local_part || ''}
                      name='email_local_part'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />

                    <span
                      style={{
                        paddingTop: '36px',
                        fontWeight: 'bold',
                        fontSize: '18px',
                      }}>
                      @
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ paddingTop: '18px' }}>
                        <div
                          className='custom-select'
                          style={{ marginTop: '0px' }}>
                          <FormControl
                            variant='standard'
                            disabled={inputsDisabled}
                            fullWidth>
                            <InputLabel id='email-domain'>
                              {t('emailTemplates.emailSender.emailDomain')}
                            </InputLabel>
                            <Select
                              labelId='email-domain'
                              value={formData.email_domain_id || ''}
                              onChange={handleEmailDomainChange}
                              name='email_domain_id'
                              className='full-width-elem'>
                              {organizationDomains &&
                              organizationDomains.length > 0 ? (
                                organizationDomains.map((obj) => (
                                  <MenuItem
                                    key={obj.id}
                                    value={obj.id?.toString()}>
                                    {obj.domain}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem disabled>
                                  No domains available
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        marginTop: '20px',
                      }}>
                      <InfoTooltip
                        title={t('emailTemplates.emailSender.description')}
                      />
                    </div>
                  </div>
                </GridItem>
              </GridContainer>
            </GridItem>
          </GridContainer>
        </form>
      </CardBody>
      {!isCreate && (
        <CardFooter style={{ justifyContent: 'flex-end' }}>
          {!isEditingAudience && (
            <Button color='primary' onClick={handleEnterEditMode}>
              {t('common.buttons.edit')}
            </Button>
          )}
          {isEditingAudience && (
            <>
              <Button
                color='rose'
                onClick={handleCancelEdit}
                style={{ marginRight: '10px' }}>
                {t('common.buttons.cancel')}
              </Button>
              <Button color='primary' onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress
                      size={20}
                      color='inherit'
                      style={{ marginRight: '8px' }}
                    />
                    <span>{t('common.saving')}</span>
                  </div>
                ) : (
                  t('common.buttons.save')
                )}
              </Button>
            </>
          )}
        </CardFooter>
      )}
    </Card>
  );
}
